import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { flattenPages } from '@/api/pagination'
import { JOB_STATES, type AssetJobDto } from '@/api/types'
import {
  assetJobsQuery,
  useReconcileAssets,
  useRetryJob,
} from '@/features/platform/platform.queries'
import { formatRelative } from '@/lib/format'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  PageHeader,
  useToast,
  type Column,
} from '@/ui'
import { IconArrowBack, IconRetry } from '@/icons'

// The backend enum has no PENDING — a queued job is QUEUED. Filtering on the wrong word
// would have quietly matched nothing.
const searchSchema = z.object({ status: z.enum(JOB_STATES).optional() })

export const Route = createFileRoute('/_authed/assets/jobs')({
  validateSearch: searchSchema,
  component: JobsPage,
})

function JobsPage() {
  const { t, i18n } = useTranslation()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const toast = useToast()

  const query = useInfiniteQuery(assetJobsQuery(search.status))
  const { items, total } = flattenPages<AssetJobDto>(query.data)

  const retry = useRetryJob()
  const reconcile = useReconcileAssets()
  const [isReconciling, setIsReconciling] = useState(false)

  const columns: Column<AssetJobDto>[] = [
    {
      id: 'asset',
      header: t('assets.columnAsset'),
      cell: (job) => <span className="truncate font-medium text-text">{job.assetName}</span>,
    },
    {
      id: 'kind',
      header: t('assets.columnType'),
      cell: (job) => <Badge>{job.step}</Badge>,
    },
    {
      id: 'status',
      header: t('assets.jobStatus'),
      cell: (job) => (
        <Badge
          tone={job.state === 'FAILED' ? 'danger' : job.state === 'DONE' ? 'success' : 'warning'}
          hasDot
        >
          {job.state}
        </Badge>
      ),
    },
    {
      id: 'attempts',
      header: t('assets.jobAttempts'),
      align: 'end',
      cell: (job) => <span data-numeric>{job.attemptCount}</span>,
    },
    {
      id: 'error',
      header: t('assets.jobError'),
      isSecondary: true,
      cell: (job) =>
        job.failureCode ? (
          // The code is the stable part; the detail is prose and goes in the tooltip.
          <span
            className="truncate font-mono text-xs text-danger"
            title={job.failureDetail ?? job.failureCode}
          >
            {job.failureCode}
          </span>
        ) : (
          <span className="text-text-muted">—</span>
        ),
    },
    {
      id: 'updated',
      header: t('audit.columnWhen'),
      align: 'end',
      cell: (job) => (
        <span className="text-text-secondary">{formatRelative(job.updatedAt, i18n.language)}</span>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{t('common.actions')}</span>,
      align: 'end',
      cell: (job) =>
        job.state === 'FAILED' ? (
          <Button
            variant="ghost"
            size="sm"
            icon={<IconRetry size={14} />}
            isLoading={retry.isPending && retry.variables === job.id}
            onClick={() => {
              retry.mutate(job.id, {
                onSuccess: () => {
                  toast.success(t('assets.retried'))
                },
                onError: (error) => {
                  toast.fromError(error, t('state.errorTitle'))
                },
              })
            }}
          >
            {t('assets.retryJob')}
          </Button>
        ) : null,
    },
  ]

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link
            to="/assets"
            search={{ q: '', sort: 'createdAt', order: 'desc' }}
            className="mb-2 inline-flex items-center gap-1 text-[0.8125rem] text-text-secondary hover:text-text"
          >
            <IconArrowBack size={14} />
            {t('assets.title')}
          </Link>
        }
        title={t('assets.jobsTitle')}
        description={t('assets.jobsSubtitle')}
        actions={
          <Button
            variant="secondary"
            icon={<IconRetry size={15} />}
            onClick={() => {
              setIsReconciling(true)
            }}
          >
            {t('assets.reconcile')}
          </Button>
        }
      />

      <Card isFlush>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <label className="sr-only" htmlFor="job-status">
            {t('assets.jobStatus')}
          </label>
          <select
            id="job-status"
            value={search.status ?? ''}
            onChange={(event) => {
              void navigate({
                search: {
                  status: (event.target.value || undefined) as (typeof JOB_STATES)[number] | undefined,
                },
              })
            }}
            className="h-9 rounded-control border border-border-strong/60 bg-surface px-2.5 text-[0.8125rem] text-text"
          >
            <option value="">{t('common.all')}</option>
            {JOB_STATES.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>

          {total > 0 ? (
            <span data-numeric className="ml-auto text-[0.8125rem] text-text-secondary">
              {t('common.showing', { count: items.length, total })}
            </span>
          ) : null}
        </div>

        <DataTable
          columns={columns}
          rows={items}
          getRowId={(job) => job.id}
          caption={t('assets.jobsTitle')}
          isLoading={query.isPending}
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
          emptyTitle={t('assets.noJobsPending')}
        />
      </Card>

      <ConfirmDialog
        isOpen={isReconciling}
        onClose={() => {
          setIsReconciling(false)
        }}
        onConfirm={() => {
          reconcile.mutate(undefined, {
            onSuccess: (result) => {
              toast.success(
                result.queued > 0
                  ? t('assets.reconciled', { count: result.queued })
                  : t('assets.reconciledNone'),
              )
              setIsReconciling(false)
            },
            onError: (error) => {
              toast.fromError(error, t('state.errorTitle'))
            },
          })
        }}
        title={t('assets.reconcileConfirm')}
        body={t('assets.reconcileBody')}
        confirmLabel={t('assets.reconcile')}
        cancelLabel={t('common.cancel')}
        isDestructive={false}
        isPending={reconcile.isPending}
      />
    </>
  )
}
